<template>
  <div class="template-selector">
    <div class="badgebutton-group">
      <button class="badgebutton active text-center" @click="$emit('add-blank')">
        <i class="far fa-plus"></i>
        Add new Task
      </button>
      <button
        class="badgebutton active text-center"
        @click="toggleDropdown"
        ref="dropdownToggle"
      >
        <i class="far fa-caret-down"></i>
      </button>
    </div>

    <div v-show="isOpen" class="template-dropdown" ref="dropdown">
      <div class="dropdown-header">
        <div class="search-container">
          <i class="far fa-search search-icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search templates..."
            class="search-input"
            ref="searchInput"
            @keydown.enter="handleEnter"
            @keydown.escape="closeDropdown"
          />
        </div>
      </div>

      <div class="dropdown-content">
        <!-- No templates message -->
        <div v-if="!hasAnyTemplates" class="no-templates">
          <i class="far fa-2x fa-sticky-note"></i>
          <br />
          There are no templates for this board available.
        </div>

        <!-- Search results (flat list) -->
        <div v-else-if="searchQuery" class="search-results">
          <div v-if="!filteredTemplates.length" class="no-results">
            No templates found matching "{{ searchQuery }}"
          </div>
          <div
            v-for="template in filteredTemplates"
            :key="template.id"
            class="template-item"
            @click="selectTemplate(template.id)"
          >
            <i class="far fa-file-alt template-icon"></i>
            <span class="template-title">{{ template.title }}</span>
            <span class="click-hint">click to add</span>
          </div>
        </div>

        <!-- Categorized view -->
        <div v-else class="category-list">
          <!-- Flat list when only uncategorized templates -->
          <template v-if="hasOnlyUncategorized">
            <div
              v-for="templateItem in categoryGroups[0].templates"
              :key="templateItem.template.id"
              class="template-item"
              @click="selectTemplate(templateItem.template.id)"
            >
              <i class="far fa-file-alt template-icon"></i>
              <span class="template-title">{{ templateItem.template.title }}</span>
              <span class="click-hint">click to add</span>
            </div>
          </template>

          <!-- Category groups with hover submenus -->
          <template v-else>
            <div
              v-for="(group, index) in categoryGroups"
              :key="group.category ? group.category.guid : 'uncategorized'"
              class="category-group"
              :ref="'category-' + index"
              @mouseenter="onCategoryHover(group, index, $event)"
              @mouseleave="onCategoryLeave"
            >
              <div class="category-header">
                <i v-if="group.category && group.category.icon" :class="group.category.icon" class="category-icon"></i>
                <i v-else class="far fa-folder category-icon"></i>
                <span class="category-name">{{ group.category ? group.category.name : 'Uncategorized' }}</span>
                <i class="far fa-chevron-right expand-icon"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Floating submenu rendered outside the dropdown -->
    <div
      v-if="expandedCategory && expandedCategoryTemplates.length > 0"
      class="template-submenu-wrapper"
      :style="submenuStyle"
      @mouseenter="submenuHovered = true"
      @mouseleave="onSubmenuLeave"
    >
      <div class="template-submenu">
        <div
          v-for="templateItem in expandedCategoryTemplates"
          :key="templateItem.template.id"
          class="template-item"
          @click="selectTemplate(templateItem.template.id)"
        >
          <i class="far fa-file-alt template-icon"></i>
          <span class="template-title">{{ templateItem.template.title }}</span>
          <span class="click-hint">click to add</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { boardItemTemplateClient } from '@/apiclient/opsboard-api';
import { TemplateCategoryWithTemplatesDto, BoardItemTemplateWithScheduleAndAvailabilityDto } from '@/apiclient/apiclient_generated';
import { GlobalHelper } from '@/helpers/global-helper';

interface FlatTemplate {
  id: string;
  title: string;
}

@Component
export default class TemplateSelector extends Vue {
  @Prop({ required: true }) boardGuid!: string;

  private isOpen = false;
  private searchQuery = '';
  private categoryGroups: TemplateCategoryWithTemplatesDto[] = [];
  private expandedCategory: string | null = null;
  private expandedCategoryTemplates: BoardItemTemplateWithScheduleAndAvailabilityDto[] = [];
  private submenuStyle: { top: string; left: string } = { top: '0px', left: '0px' };
  private submenuHovered = false;
  private leaveTimeout: number | null = null;

  async mounted() {
    await this.loadTemplates();
    document.addEventListener('click', this.handleClickOutside);
  }

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
    }
  }

  @Watch('boardGuid')
  async onBoardGuidChange() {
    await this.loadTemplates();
  }

  async loadTemplates() {
    if (!this.boardGuid) {
      return;
    }
    try {
      this.categoryGroups = await boardItemTemplateClient.getListForBoard(this.boardGuid);
    } catch (error) {
      console.error(error);
      GlobalHelper.NotifyError('Failed to load templates');
    }
  }

  get hasAnyTemplates(): boolean {
    return this.categoryGroups.some(g => g.templates && g.templates.length > 0);
  }

  get hasOnlyUncategorized(): boolean {
    return this.categoryGroups.length === 1 && !this.categoryGroups[0].category;
  }

  get allTemplates(): FlatTemplate[] {
    const templates: FlatTemplate[] = [];
    for (const group of this.categoryGroups) {
      if (group.templates) {
        for (const t of group.templates) {
          if (t.template) {
            templates.push({
              id: t.template.id!,
              title: t.template.title || ''
            });
          }
        }
      }
    }
    return templates;
  }

  get filteredTemplates(): FlatTemplate[] {
    if (!this.searchQuery) return [];
    const query = this.searchQuery.toLowerCase();
    return this.allTemplates.filter(t => t.title.toLowerCase().includes(query));
  }

  onCategoryHover(group: TemplateCategoryWithTemplatesDto, index: number, event: MouseEvent) {
    if (this.leaveTimeout) {
      clearTimeout(this.leaveTimeout);
      this.leaveTimeout = null;
    }

    const categoryKey = group.category ? group.category.guid : 'uncategorized';
    this.expandedCategory = categoryKey!;
    this.expandedCategoryTemplates = group.templates || [];

    // Calculate position based on the hovered element
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const dropdown = this.$refs.dropdown as HTMLElement;
    const dropdownRect = dropdown?.getBoundingClientRect();

    this.submenuStyle = {
      top: `${rect.top}px`,
      left: `${dropdownRect ? dropdownRect.right : rect.right}px`
    };
  }

  onCategoryLeave() {
    // Delay closing to allow moving to submenu
    this.leaveTimeout = window.setTimeout(() => {
      if (!this.submenuHovered) {
        this.expandedCategory = null;
        this.expandedCategoryTemplates = [];
      }
    }, 100);
  }

  onSubmenuLeave() {
    this.submenuHovered = false;
    this.expandedCategory = null;
    this.expandedCategoryTemplates = [];
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchQuery = '';
      this.expandedCategory = null;
      this.expandedCategoryTemplates = [];
      this.$nextTick(() => {
        (this.$refs.searchInput as HTMLInputElement)?.focus();
      });
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.searchQuery = '';
    this.expandedCategory = null;
    this.expandedCategoryTemplates = [];
  }

  handleClickOutside(event: MouseEvent) {
    const dropdown = this.$refs.dropdown as HTMLElement;
    const toggle = this.$refs.dropdownToggle as HTMLElement;
    const target = event.target as Node;

    // Check if click is inside submenu
    const submenu = this.$el.querySelector('.template-submenu');
    if (submenu && submenu.contains(target)) {
      return;
    }

    if (
      dropdown &&
      toggle &&
      !dropdown.contains(target) &&
      !toggle.contains(target)
    ) {
      this.closeDropdown();
    }
  }

  handleEnter() {
    if (this.filteredTemplates.length === 1) {
      this.selectTemplate(this.filteredTemplates[0].id);
    }
  }

  selectTemplate(templateId: string) {
    this.$emit('select', templateId);
    this.closeDropdown();
  }
}
</script>

<style scoped lang="scss">
.template-selector {
  position: relative;
  display: inline-block;
}

.badgebutton-group {
  display: flex;
  align-items: center;
}

.template-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  min-width: 300px;
  max-width: 400px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
  color: #212529;
  font-size: 13px;
}

.dropdown-header {
  padding: 8px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 4px 4px 0 0;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 5px 10px 5px 28px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
}

.dropdown-content {
  max-height: 400px;
  overflow-y: auto;
}

.no-templates,
.no-results {
  padding: 20px;
  text-align: center;
  color: #6c757d;
}

.search-results {
  padding: 4px 0;
}

.category-list {
  padding: 4px 0;
}

.category-group {
  display: block;
}

.category-header {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #dee2e6;

  &:hover {
    background-color: #f8f9fa;
  }
}

.category-group:last-child .category-header {
  border-bottom: none;
}

.category-icon {
  width: 20px;
  margin-right: 8px;
  color: #6c757d;
}

.category-name {
  flex: 1;
  font-weight: 500;
}

.expand-icon {
  color: #6c757d;
  font-size: 0.75rem;
}

.template-submenu-wrapper {
  position: fixed;
  z-index: 1002;
  padding: 45px 0;
  margin-top: -45px;
}

.template-submenu {
  min-width: 280px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
  color: #212529;
  font-size: 13px;
}

.template-item {
  display: flex;
  align-items: center;
  padding: 10px 10px;
  border-bottom: 1px solid #dee2e6;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &:nth-child(odd) {
    background-color: #f9f9f9;
  }

  &:nth-child(even) {
    background-color: #fff;
  }

  &:hover {
    border-left-color: #a0a0a0;
  }

  &:last-child {
    border-bottom: none;
  }
}

.template-icon {
  width: 16px;
  margin-right: 10px;
  color: #6c757d;
}

.template-title {
  flex: 1;
  margin-right: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.click-hint {
  color: #a0a0a0;
  font-style: italic;
  font-size: 12px;
  visibility: hidden;
}

.template-item:hover .click-hint {
  visibility: visible;
}
</style>
